import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { server } from "../server";
import Footer from "./Footer";
import Header from "./Header";
import { Link } from "react-router-dom";
import GLightbox from "glightbox";
import { setAuthRedirectIntent } from "../utils/authRedirect";

function Search() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [stream, setStream] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const token = localStorage.getItem("authToken");
  const [searchResults, setSearchResults] = useState([]);
  const cameraErrorShown = useRef(false);
  const isLoggedIn = !!token;
  const visibleCount = Math.max(1, Math.ceil(searchResults.length * 0.2));

  // ✅ Start Camera
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });
      setStream(mediaStream);

      // slight delay to ensure DOM is ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(() => {}); // avoid autoplay errors
        }
      }, 300);

      setCameraActive(true);
    } catch (err) {
      if (!cameraErrorShown.current) {
        toast.error("Camera Access Denied");
        cameraErrorShown.current = true;
      }
    }
  };

  // ✅ Stop Camera
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
      setCameraActive(false);
    }
  };

  // ✅ Capture + Upload Selfie
  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || video.readyState < 2) {
      toast.info("Please Wait, Starting Camera", { autoClose: 2000 });
      return;
    }

    const ctx = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageDataUrl = canvas.toDataURL("image/jpeg");
    setCapturedImage(imageDataUrl);

    // Stop the camera after capture
    stopCamera();

    canvas.toBlob(async (blob) => {
      const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });

      const toastId = toast.loading("Uploading Selfie, Please Wait");
      try {
        await uploadAndSearch(file);
        toast.update(toastId, {
          render: "Selfie Uploaded",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
      } catch (err) {
        toast.update(toastId, {
          render: "Something Went Wrong",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    }, "image/jpeg");
  };

  // ✅ Upload + Search API
  const uploadAndSearch = async (file) => {
    try {
      const fileName = `selfie-${Date.now()}.jpg`;
      const presignedRes = await fetch(
        `${server}/PresignedUrl/GeneratePresignedUrl`,
        {
          method: "POST",
          headers: {
            Authorization: token,
          },
          body: JSON.stringify({
            folder: "search",
            fileNames: [fileName],
          }),
        }
      );

      const presignedData = await presignedRes.json();
      const uploadDetails = presignedData.urls[0];
      const s3Key = uploadDetails.imageKey;

      await fetch(uploadDetails.signedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": uploadDetails.contentType,
        },
        body: file,
      });

      const searchRes = await fetch(
        `${server}/Search/GlobalSearch?key=${encodeURIComponent(s3Key)}`,
        {
          method: "GET",
        }
      );

      const searchData = await searchRes.json();

      if (searchData.images && searchData.images.length > 0) {
        setSearchResults(searchData.images);
      } else {
        toast.info("No Matches Found");
      }
    } catch (err) {
      toast.error("Something Went Wrong");
    }
  };

  useEffect(() => {
    const lightbox = GLightbox({
      selector: ".glightbox",
    });

    return () => lightbox.destroy();
  }, [searchResults]);

  return (
    <>
      <Header />

      <section>
        <div className="container mb-3 mt-4 text-center">
          <h4 className="text-white lh-sm">Find Your Photos Instantly</h4>
          <p className="text-white opacity-75 mb-4 lh-sm">
            Upload a selfie to instantly see every event photo you’re in—no
            tags, no search.
          </p>

          <div className="mt-5 d-flex flex-column align-items-center justify-content-center">
            <div
              className="rounded-circle overflow-hidden bg-light-primary d-flex align-items-center justify-content-center"
              style={{ width: 220, height: 220 }}
            >
              {cameraActive ? (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-100 h-100 object-fit-cover rounded-circle"
                  onCanPlay={() => videoRef.current?.play()}
                />
              ) : capturedImage ? (
                <img
                  src={capturedImage}
                  width="220"
                  height="220"
                  className="object-fit-cover rounded-circle"
                  alt="Captured Selfie"
                />
              ) : (
                <i className="bi bi-camera display-1"></i>
              )}
            </div>

            <canvas ref={canvasRef} style={{ display: "none" }} />

            <div className="mt-5">
              {!cameraActive && !capturedImage ? (
                <button
                  className="btn btn-dark-primary border-0 px-5 rounded-3 fs-6 fw-medium"
                  onClick={startCamera}
                >
                  <i className="bi bi-camera me-1"></i> Capture Selfie
                </button>
              ) : (
                <button
                  className="btn btn-dark-primary border-0 px-5 rounded-3 fs-6 fw-medium"
                  onClick={capturePhoto}
                  disabled={!stream}
                >
                  <i className="bi bi-upload me-1"></i> Upload Selfie
                </button>
              )}

              <p className="mt-2 text-white opacity-75 fs-7 text-center">
                By uploading your selfie, you agree to our{" "}
                <Link
                  to="/tnc"
                  className="text-decoration-underline text-white"
                >
                  Terms & Conditions
                </Link>
                .
              </p>
            </div>

            {searchResults.length > 0 && (
              <div className="mt-3 container">
                <h5 className="text-light mb-4">
                  Search Results ({searchResults.length}) Images
                </h5>
                <div className="row g-2 mb-lg-4">
                  {searchResults.map((item, index) => {
                    const isBlurred = !isLoggedIn && index >= visibleCount;

                    return (
                      <div
                        className="col-lg-1 col-md-3 col-4 position-relative"
                        key={index}
                      >
                        {isBlurred ? (
                          <>
                            <img
                              src={item.imageUrl}
                              alt={`blurred-${index}`}
                              className="w-100 object-fit-cover rounded-4"
                              height="100"
                              style={{
                                filter: "blur(4px)",
                                pointerEvents: "none",
                              }}
                            />
                          </>
                        ) : (
                          <a href={item.imageUrl} className="glightbox">
                            <img
                              src={item.imageUrl}
                              alt={`match-${index}`}
                              className="w-100 object-fit-cover rounded-4"
                              height="100"
                              loading="lazy"
                            />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
                <>
                  {searchResults.length > 0 && !isLoggedIn && (
                    <Link
                      to="/login"
                      onClick={() =>
                        setAuthRedirectIntent({
                          source: "search",
                          target: "/my-photos",
                          fallback: "/dashboard",
                        })
                      }
                      className="btn btn-success px-7 rounded-pill fw-normal btn-sm mt-3"
                    >
                      Login / Register to View All Images
                    </Link>
                  )}
                </>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default Search;
